import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { UserContext } from "./UserContext";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

const extractSyllabusArray = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  const possibleKeys = [
    "data",
    "result",
    "items",
    "content",
    "payload",
    "syllabus",
  ];

  for (const key of possibleKeys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  if (payload?.data) {
    if (Array.isArray(payload.data?.items)) {
      return payload.data.items;
    }
    if (Array.isArray(payload.data?.result)) {
      return payload.data.result;
    }
  }

  return [];
};

const normalizeGradeValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return String(value);
  }

  const strValue = String(value).trim();
  if (!strValue) {
    return null;
  }

  const numericMatch = strValue.match(/\d+/);
  if (numericMatch) {
    return numericMatch[0];
  }

  return strValue;
};

export const SyllabusContext = createContext();

export const SyllabusProvider = ({ children }) => {
  const [syllabusData, setSyllabusData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectChapters, setSubjectChapters] = useState({});
  const resetSyllabusState = () => {
    setSyllabusData([]);
    setSubjects([]);
    setSubjectChapters({});
    setError(null);
    setDontfetchSyllabus(true);
    setIsLoading(false);
  };

  // Get user context to access grade
  const userContext = useContext(UserContext);
  const { userData } = userContext || {};
  const { grade } = userData || {};

  // Process syllabus data to extract subjects and chapters for user's grade
  const processSyllabusData = (data, userGrade) => {
    if (!Array.isArray(data) || data.length === 0) {
      return { subjects: [], subjectChapters: {} };
    }

    const normalizedUserGrade = normalizeGradeValue(userGrade);

    const matchesGrade = (item) => {
      if (!normalizedUserGrade) {
        return true;
      }
      const itemGrade = normalizeGradeValue(
        item?.grade ?? item?.standard ?? item?.class ?? item?.gradeLevel
      );
      // If API entry has no grade metadata, keep it so users still see the content
      if (!itemGrade) {
        return true;
      }
      return itemGrade === normalizedUserGrade;
    };

    const gradeFilteredData = normalizedUserGrade
      ? data.filter((item) => matchesGrade(item))
      : data;

    const sourceData =
      normalizedUserGrade && gradeFilteredData.length === 0
        ? data
        : gradeFilteredData;

    const chaptersMap = {};
    const subjectsSet = new Set();

    sourceData.forEach((item) => {
      const subjectName =
        typeof item?.subject === "string" ? item.subject.trim() : "";

      if (!subjectName) {
        return;
      }

      subjectsSet.add(subjectName);

      if (!chaptersMap[subjectName]) {
        chaptersMap[subjectName] = [];
      }

      if (Array.isArray(item.chapters)) {
        item.chapters.forEach((chapter) => {
          const chapterName =
            typeof chapter === "string"
              ? chapter.trim()
              : (chapter?.name || chapter?.title || "").trim();

          if (chapterName && !chaptersMap[subjectName].includes(chapterName)) {
            chaptersMap[subjectName].push(chapterName);
          }
        });
      }
    });

    subjectsSet.forEach((subject) => {
      if (!chaptersMap[subject]) {
        chaptersMap[subject] = [];
      }
    });

    return {
      subjects: Array.from(subjectsSet),
      subjectChapters: chaptersMap,
    };
  };

  // Fetch syllabus data
  const fetchSyllabus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${baseUrl}/api/v2/syllabus`, {});
      const normalizedData = extractSyllabusArray(response.data);
      console.log("Fetched syllabus data:", normalizedData);
      setSyllabusData(normalizedData);
      const { subjects, subjectChapters } = processSyllabusData(
        normalizedData,
        grade
      );
      console.log("Processed syllabus data:", { subjects, subjectChapters });
      setSubjects(subjects);
      setSubjectChapters(subjectChapters);
      // Process data if grade is available
      // if (grade) {
      //   const { subjects, subjectChapters } = processSyllabusData(data, grade);
      //   setSubjects(subjects);
      //   setSubjectChapters(subjectChapters);
      // }
    } catch (err) {
      console.error("Error fetching syllabus:", err);
      setError(err.message);
      setSyllabusData([]);
      setSubjects([]);
      setSubjectChapters({});
    } finally {
      setIsLoading(false);
    }
  };

  // Get chapters for a specific subject
  const getChaptersForSubject = (subject) => {
    if (!subject) {
      // Return all chapters from all subjects if no subject is specified
      const allChapters = [];
      Object.values(subjectChapters).forEach((chapters) => {
        allChapters.push(...chapters);
      });
      return [...new Set(allChapters)];
    }
    return subjectChapters[subject] || [];
  };

  // Re-process syllabus data when grade changes
  const [dontfetchSyllabus, setDontfetchSyllabus] = useState(false);
  useEffect(() => {
    if (dontfetchSyllabus) {
      setDontfetchSyllabus(false);
      return;
    }
    if (!Array.isArray(syllabusData) || syllabusData.length === 0) {
      setSubjects([]);
      setSubjectChapters({});
      return;
    }

    const { subjects: nextSubjects, subjectChapters: nextChapters } =
      processSyllabusData(syllabusData, grade);
    setSubjects(nextSubjects);
    setSubjectChapters(nextChapters);
  }, [grade, syllabusData]);

  // Fetch syllabus data on mount
  useEffect(() => {
    fetchSyllabus();
  }, [baseUrl]);
  useEffect(() => {
    console.log("SyllabusProvider subjects changed:", subjects);
  }, [subjects]);

  useEffect(() => {
    console.log("SyllabusProvider syllabusData changed:", syllabusData);
  }, [syllabusData]);
  return (
    <SyllabusContext.Provider
      value={{
        syllabusData,
        isLoading,
        error,
        subjects,
        subjectChapters,
        getChaptersForSubject,
        fetchSyllabus,
        resetSyllabusState,
      }}
    >
      {children}
    </SyllabusContext.Provider>
  );
};
